/**
 * Spatial Utilities
 *
 * Shared coordinate and distance calculations used across
 * wayfinding, path validation, and network building.
 */

import { distance as turfDistance, point } from "@turf/turf";

/**
 * Coordinate equality tolerance, in decimal degrees. ~1.1m at the equator
 * (degrees-of-latitude are ~111km, so 0.00001° ≈ 1.11m). Smaller at higher
 * latitudes for longitude-of-degrees but conservatively bounded by lat.
 *
 * Aligned with snap-on-draw's 18px tolerance at the typical zoom levels admins
 * draw at (z17-z19, where 18px ≈ 0.5-2m at the equator). Two coords within
 * this tolerance are considered "the same point" by the routing graph,
 * smoothing pin set, issue dedupe, and any other equality-by-location code.
 *
 * If you change this, also update the diagnostic in
 * `apps/api/scripts/diagnose-coord-precision.ts` to match.
 */
export const COORD_TOLERANCE = 0.00001;

/**
 * Calculate distance between two coordinates in meters
 */
export function calcDistance(
  from: [number, number],
  to: [number, number]
): number {
  return turfDistance(point(from), point(to), { units: "meters" });
}

/**
 * Canonical "are these coords the same point?" key. Rounds to COORD_TOLERANCE
 * before formatting so two coords within tolerance produce byte-identical
 * strings. Used by:
 *   - the routing graph (`buildPathNetwork` node IDs in pathNetwork.ts)
 *   - smoothing's junction pin set (`splitAtPinned` in smoothing.ts)
 *   - path-editor endpoint dedupe (`usePathEditor`)
 *   - path-issue dedupe IDs (`pathIssues`)
 *
 * SINGLE SOURCE OF TRUTH. If you find another file rolling its own
 * `coord.toFixed(N)` or `Math.round(c / 0.0000X)` for coord identity,
 * migrate it here. Mismatches between any two precision systems silently
 * fragment graph nodes during publish-time smoothing (see Plan 115 wrap-up
 * for the bug this fixes).
 */
export function coordToId(coord: [number, number]): string {
  const lng = Math.round(coord[0] / COORD_TOLERANCE) * COORD_TOLERANCE;
  const lat = Math.round(coord[1] / COORD_TOLERANCE) * COORD_TOLERANCE;
  return `${lng.toFixed(6)},${lat.toFixed(6)}`;
}

/**
 * Calculate bearing between two coordinates in degrees (0-360)
 */
export function calcBearing(
  from: [number, number],
  to: [number, number]
): number {
  const dLng = ((to[0] - from[0]) * Math.PI) / 180;
  const lat1 = (from[1] * Math.PI) / 180;
  const lat2 = (to[1] * Math.PI) / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  let bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
}
